import round from 'lodash/round'
import { FPS } from '../types/video'

/**
 * The one seconds → frames policy (#77).
 *
 * Persisted timing is seconds; Remotion wants whole frames. Every conversion
 * goes through here so preview, Lambda and `Render.duration` agree:
 *
 * - Seconds are rounded to milliseconds first. No millisecond value sits on a
 *   half-frame boundary at 30 fps ((2k+1)/60 s is never a whole ms), so float
 *   noise in `a.from + a.duration` versus `b.from` cannot round two contiguous
 *   items to different frames.
 * - An item's END is quantized, not its duration: `durationInFrames` is
 *   `frames(from + duration) − frames(from)`, floored at one frame. Rounding
 *   is not distributive over addition, and quantizing `duration` on its own
 *   opens one-frame gaps or overlaps between items that were contiguous in
 *   seconds.
 * - The document length is the frame-space maximum of item ends, never
 *   `round(maxSeconds * fps)` — the spike's L190-260 cases pin why.
 */

export function secondsToFrames(seconds: number, fps: number = FPS): number {
  return Math.round(round(seconds, 3) * fps)
}

export function framesToSeconds(frames: number, fps: number = FPS): number {
  return round(frames / fps, 3)
}

export type FrameSpan = { from: number; durationInFrames: number }

export function itemFrames(
  item: { from: number; duration: number },
  fps: number = FPS,
): FrameSpan {
  const from = secondsToFrames(item.from, fps)
  const end = secondsToFrames(item.from + item.duration, fps)
  return { from, durationInFrames: Math.max(1, end - from) }
}

/** The composition length in frames: the last frame any item occupies, plus one. */
export function docDurationInFrames(items: Iterable<FrameSpan>): number {
  let max = 0
  for (const item of items) {
    max = Math.max(max, item.from + item.durationInFrames)
  }
  return max
}
